import { useRef, useState } from "react";
import type { Settings, Vehicle } from "../types";
import { calculateTool } from "../lib/toolMath";
import { toolText, type ToolText } from "../lib/toolCopy";
import { SectionHeader } from "./SectionHeader";
import { VehicleInventory } from "./VehicleInventory";
import "./tools.css";

export type ToolId = "battery" | "charging" | "range" | "buying" | "session" | "warranty" | "fuel";
type Result = Record<string, number | string>;
type Field = [ToolText, string | number, number?, number?, string?];
export function IntegratedTools({ tool, vehicle, settings, onBack, apiBase }: {
  tool: ToolId; vehicle?: Vehicle; settings: Settings; onBack: () => void; apiBase: string;
}) {
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState("");
  const [reading, setReading] = useState(false);
  const [fileStatus, setFileStatus] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const resultRef = useRef<HTMLElement>(null);
  const tr = (key: ToolText) => toolText(settings.language, key);
  const fmt = (value: number) => new Intl.NumberFormat(settings.language, { maximumFractionDigits: 1 }).format(value);
  const money = (value: number) => new Intl.NumberFormat(settings.language, { style: "currency", currency: settings.currency }).format(value);
  const cap = vehicle?.batteryCapacity || 75, use = vehicle?.consumption || 18;
  const fields: Record<Exclude<ToolId, "buying">, Field[]> = {
    battery: [["production", vehicle?.firstRegistration?.slice(0,7) || "", undefined, undefined, "month"], ["mileage", vehicle?.mileage ?? "", 0], ["soh", vehicle?.soh ?? "", 1, 100], ["delta", vehicle?.delta ?? "", 0, 2000]],
    charging: [["km", 1200, 1], ["use", use, .1, 100], ["electricity", .25, 0], ["litres", 7, .1, 100], ["fuelPrice", 1.85, 0]],
    range: [["capacity", cap, .1, 500], ["soh", vehicle?.soh || 100, 1, 100], ["use", use, .1, 100], ["speed", 95, 1, 250], ["temperature", 18, -50, 60], ["climate", 5, 0, 30]],
    session: [["capacity", cap, .1, 500], ["power", 11, .1, 1000], ["start", 20, 0, 100], ["end", 80, 0, 100], ["price", .25, 0], ["efficiency", 90, 1, 100], ["use", use, .1, 100]],
    warranty: [["first", "", undefined, undefined, "month"], ["years", "", 1, 30], ["limit", "", 1], ["mileage", vehicle?.mileage ?? "", 0]],
    fuel: [["startKm", "", 0], ["endKm", "", 0], ["volume", "", .1, 500], ["fuelPrice", 1.85, 0]],
  };
  if (tool === "buying") return <VehicleInventory onClose={onBack} language={settings.language} apiBase={apiBase}/>;
  const help = tr((tool + "Help") as ToolText), next = tr((tool + "Next") as ToolText);

  async function analyseDocument(file?: File) {
    if (!file || reading) return;
    setReading(true); setFileStatus(tr("reading")); setResult(null);
    try {
      if (file.size > 20 * 1024 * 1024) throw new Error("size");
      let text = "";
      if (file.type === "application/pdf") {
        const { getPdf } = await import("../lib/pdf");
        const pdf = await getPdf({ data: await file.arrayBuffer() });
        try {
          for (let p = 1; p <= Math.min(pdf.numPages, 20); p++) {
            const page = await pdf.getPage(p);
            text += " " + (await page.getTextContent()).items.map(i => "str" in i ? i.str : "").join(" ");
          }
        } finally { await pdf.loadingTask.destroy(); }
      } else {
        if (!file.type.startsWith("image/")) throw new Error("format");
        if (!(window as any).Tesseract) await new Promise<void>((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";
          script.onload = () => resolve(); script.onerror = reject; document.head.appendChild(script);
        });
        text = (await (window as any).Tesseract.recognize(file, "eng+deu+por")).data.text;
      }
      const matches = {
        soh: text.match(/(?:SOH|State\s*of\s*Health)[^0-9]{0,18}(\d{1,3}(?:[.,]\d+)?)/i),
        delta: text.match(/(?:Delta|imbalance|Zell[^\n]{0,12})[^0-9]{0,18}(\d{1,4}(?:[.,]\d+)?)\s*mV/i),
        mileage: text.match(/([0-9 .'’]{4,})\s*km/i),
      };
      let found = false;
      Object.entries(matches).forEach(([key, match]) => {
        if (!match) return;
        const input = formRef.current?.elements.namedItem(key) as HTMLInputElement | null;
        if (input) { input.value = key === "mileage" ? match[1].replace(/[^0-9]/g, "") : match[1].replace(",", "."); found = true; }
      });
      setFileStatus(tr(found ? "review" : "readError"));
    } catch { setFileStatus(tr("readError")); }
    finally { setReading(false); }
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault(); setError("");
    const values = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    try {
      let computed: Result;
      if (tool === "battery") {
        calculateTool("warranty", { first: values.production, years: 30, limit: 1e8, mileage: values.mileage });
        const soh = Number(values.soh), delta = Number(values.delta);
        if (!values.soh || !values.delta || !Number.isFinite(soh) || soh < 1 || soh > 100 || !Number.isFinite(delta) || delta < 0 || delta > 2000) throw new Error("soh");
        computed = { soh, delta, mileage: Number(values.mileage) };
      } else if (tool !== "buying") computed = calculateTool(tool, values);
      else return;
      setResult(computed);
      requestAnimationFrame(() => resultRef.current?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "start" }));
    } catch (err) {
      setResult(null); setError(tr("invalid"));
      const input = formRef.current?.elements.namedItem(err instanceof Error ? err.message : "") as HTMLInputElement | null;
      input?.focus();
    }
  }
  const metric = (label: string, value: string) => <article key={label}><span>{label}</span><strong>{value}</strong></article>;
  const n = (key: string) => Number(result?.[key] || 0);
  return <section className="screen precision-tools tool-detail" data-native-i18n lang={settings.language}>
    <SectionHeader className="tool-heading" title={tr(tool)} language={settings.language} onBack={onBack}/>
    <p className="tool-description">{tr((tool + "Desc") as ToolText)}</p>
    <p className="tool-simulation">{tr("simulation")}</p>
    <div className="tool-workspace">
      <form ref={formRef} className="calculator-form" onSubmit={submit} onChange={() => { setResult(null); setError(""); }}>
        <h2>{tr("inputs")}</h2><p className="tool-hint">{tr("example")}</p>
        <div className="calculator-fields">{fields[tool].map(([name, value, min, max, type]) => <label key={name} htmlFor={"tool-" + name}>{tr(name)}{["electricity","fuelPrice","price"].includes(name) && <small>{settings.currency}</small>}<input id={"tool-" + name} name={name} type={type || "number"} inputMode={type ? undefined : "decimal"} defaultValue={value} min={min} max={max} step={type ? undefined : name === "years" ? "1" : "any"} required/></label>)}</div>
        {tool === "fuel" && <label className="tool-checkbox"><input type="checkbox" required/>{tr("fullTank")}</label>}
        {tool === "battery" && <label className="tool-upload">{tr("upload")}<input type="file" disabled={reading} accept="application/pdf,image/*" onChange={e => void analyseDocument(e.target.files?.[0])}/><small>PDF / JPEG / PNG · ≤ 20 MB · PDF ≤ 20 pages</small><p role="status">{fileStatus}</p></label>}
        {error && <p role="alert" className="tool-error">{error}</p>}
        <button className="primary-action" type="submit" disabled={reading}>{tr("calculate")} <span>→</span></button>
      </form>
      <section ref={resultRef} className={"calculator-result" + (result ? " has-result" : "")} aria-live="polite" tabIndex={-1}>
        <h2>{tr(result ? "result" : "interpretation")}</h2>
        {result && <div className="calculator-metrics">
          {tool === "battery" && <>{metric(tr("soh"), fmt(n("soh")) + "%")}{metric(tr("capacityLoss"), fmt(100-n("soh")) + "%")}{metric(tr("delta"), fmt(n("delta")) + " mV")}{metric(tr("mileage"), fmt(n("mileage")) + " km")}</>}
          {tool === "charging" && <>{metric(tr("ev") + " · " + tr("monthly"), money(n("ev")))}{metric(tr("ice") + " · " + tr("monthly"), money(n("fuel")))}{metric(tr(n("saving") >= 0 ? "savings" : "extra") + " · " + tr("monthly"), money(Math.abs(n("saving"))))}{metric(tr("annual"), money(n("annual")))}{metric(tr("ev") + " / 100 km", money(n("ev100")))}{metric(tr("ice") + " / 100 km", money(n("fuel100")))}</>}
          {tool === "range" && <>{metric(tr("range"), fmt(n("range")) + " km")}{metric(tr("reserve"), fmt(n("reserve")) + " km")}{metric(tr("capacity"), fmt(n("usable")) + " kWh")}</>}
          {tool === "session" && <>{metric(tr("time"), fmt(n("hours")) + " h")}{metric(tr("cost"), money(n("cost")))}{metric(tr("grid"), fmt(n("grid")) + " kWh")}{metric(tr("stored"), fmt(n("battery")) + " kWh")}{metric(tr("loss"), fmt(n("loss")) + " kWh")}{metric(tr("range"), fmt(n("range")) + " km")}</>}
          {tool === "fuel" && <>{metric(tr("litres"), fmt(n("consumption")) + " L/100 km")}{metric(tr("distance"), fmt(n("distance")) + " km")}{metric(tr("cost"), money(n("cost")))}{metric("100 km", money(n("cost100")))}</>}
          {tool === "warranty" && <>{metric(tr(n("expired") ? "expired" : "within"), fmt(n("months")) + " · " + tr("months"))}{metric(tr("remaining"), fmt(n("remainingKm")) + " km")}{metric(tr("until"), new Date(String(result.endDate) + "T12:00:00").toLocaleDateString(settings.language))}</>}
        </div>}
        <div className="tool-explanation"><h3>{tr("limits")}</h3><p>{help}</p></div>
        <div className="tool-next"><h3>{tr("next")}</h3><p>{next}</p></div>
      </section>
    </div>
  </section>;
}
